import type { MbcBrandContext } from "@nlc/shared-types";

import { useNavigate } from "@tanstack/react-router";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { NOT_STARTED_QUALITY } from "@/features/request/mbc-review/lib/brief-score-summary";
import { isTeamUser } from "@/features/requests/utils/roleUtils";
import { useUser } from "@/hooks/useUser";

import type { MbcRequestType } from "../lib/request-draft-api";
import type { MbcSessionIds } from "../lib/session-ids";

import { useBriefMemory } from "../hooks/useBriefMemory";
import { useLeaveMbcFlow } from "../hooks/useLeaveMbcFlow";
import { useMBCChat } from "../hooks/useMBCChat";
import { patchBrief } from "../lib/brief-api";
import { discardSession } from "../lib/draft-api";
import { buildGatePreamble } from "../lib/gate-preamble";
import { BriefSidebar } from "./BriefSidebar";
import { DiscardBriefDialog } from "./DiscardBriefDialog";
import { MBCBriefSheet } from "./MBCBriefSheet";
import { MBCChatHeader } from "./MBCChatHeader";
import { MBCChatPanel } from "./MBCChatPanel";
import { UploadAssetsModal } from "./upload-assets/UploadAssetsModal";

type MBCConversationProps = {
  brand: MbcBrandContext | null;
  designType?: number;
  projectId?: number;
  requestType?: MbcRequestType;
  resume?: MbcSessionIds;
};

export function MBCConversation({
  brand,
  designType,
  projectId,
  requestType,
  resume,
}: MBCConversationProps) {
  const navigate = useNavigate();
  const { data: userData } = useUser();
  const isTeamMember = isTeamUser(userData?.user?.roleName);

  const [isUploadOpen, setIsUploadOpen] = useState(false);
  const [isDiscardOpen, setIsDiscardOpen] = useState(false);
  const [isDiscarding, setIsDiscarding] = useState(false);
  const [uploadsInFlight, setUploadsInFlight] = useState(0);
  const trackUpload = useCallback(
    (delta: -1 | 1) => setUploadsInFlight((count) => count + delta),
    [],
  );

  const {
    error,
    isStreaming,
    messages,
    refreshSignal,
    resourceId,
    sendUserMessage,
    status,
    threadId,
  } = useMBCChat({
    brandId: brand?.id,
    designTypeId: designType,
    projectId,
    requestType,
    resume,
  });

  // Before the first send, no request exists yet (T8) so resourceId/threadId
  // are undefined — useBriefMemory requires strings, and an empty id just
  // fetches nothing meaningful until the real ids land on the next render.
  const briefMemory = useBriefMemory({
    refreshSignal,
    resourceId: resourceId ?? "",
    threadId: threadId ?? "",
  });

  const canReview = briefMemory.quality?.canSubmit === true;
  const isScoreCalculating = isStreaming || briefMemory.isSettling;
  // Before the first send there is no request, so no brief to fetch a score for.
  const quality = threadId ? briefMemory.quality : NOT_STARTED_QUALITY;

  // Publish the session ids to the URL so closing/refreshing the page can come
  // back to this thread instead of silently starting a new one. `replace` keeps
  // it out of the history stack; the ref keeps it to a single write per thread.
  const syncedThreadId = useRef<null | string>(null);

  useEffect(() => {
    // Resume mode already came from the URL — nothing to write back.
    if (resume) return;
    if (!threadId || !resourceId || syncedThreadId.current === threadId) return;
    syncedThreadId.current = threadId;

    void navigate({
      replace: true,
      search: {
        brandId: brand?.id,
        designType,
        ["project-id"]: projectId,
        ["request-type"]: requestType,
        resourceId,
        threadId,
      },
      to: "/requests/mbc",
    });
  }, [
    brand?.id,
    designType,
    navigate,
    projectId,
    requestType,
    resume,
    resourceId,
    threadId,
  ]);

  const handleReview = useCallback(() => {
    void navigate({
      replace: true,
      search: {
        brandId: brand?.id,
        designType,
        ["project-id"]: projectId,
        ["request-type"]: requestType,
        resourceId,
        threadId,
      },
      to: "/requests/mbc-review",
    });
  }, [
    brand?.id,
    designType,
    navigate,
    projectId,
    requestType,
    resourceId,
    threadId,
  ]);

  const handleRename = useCallback(
    (title: string) => {
      if (!threadId) return;

      void patchBrief(threadId, resourceId, { title })
        .catch(() => {})
        .then(() => briefMemory.refetch());
    },
    [briefMemory, resourceId, threadId],
  );

  const handleBack = useLeaveMbcFlow();

  // Switch to the Classic Brief Form: discard the MBC session, then redirect.
  const confirmSwitchToClassic = useCallback(async () => {
    setIsDiscarding(true);
    try {
      if (threadId) await discardSession(threadId);

      // `replace` drops the /requests/mbc?threadId=… entry this session wrote,
      // so Back can't land on a thread that no longer exists.
      void navigate({
        replace: true,
        search: {
          designType,
          ["project-id"]: projectId,
          ["request-type"]: requestType ?? "graphic",
        },
        to: "/requests/create",
      });
    } finally {
      setIsDiscarding(false);
      setIsDiscardOpen(false);
    }
  }, [designType, navigate, projectId, requestType, threadId]);

  // The gate steps are never sent to the agent, so they are rebuilt in front
  // of the persisted transcript — on a fresh start and on resume alike.
  const displayMessages = useMemo(
    () => [...buildGatePreamble(brand), ...messages],
    [brand, messages],
  );

  return (
    <>
      <MBCChatHeader
        brandId={brand?.id}
        designType={designType}
        isTeamMember={isTeamMember}
        onBack={handleBack}
        onRename={threadId ? handleRename : undefined}
        onSwitchToClassic={() => setIsDiscardOpen(true)}
        requestName={briefMemory.memory?.title || undefined}
      />

      <MBCBriefSheet
        canAddFiles={Boolean(threadId)}
        isCalculating={isScoreCalculating}
        memory={briefMemory.memory}
        onAddAssets={() => setIsUploadOpen(true)}
        onFilesChanged={() => void briefMemory.recheckFiles()}
        quality={quality}
      />

      <div className="flex min-h-0 flex-1 gap-3xl p-md lg:p-3xl">
        <MBCChatPanel
          canReview={canReview}
          isStreaming={isStreaming}
          isUploading={uploadsInFlight > 0}
          messages={displayMessages}
          onAttach={() => setIsUploadOpen(true)}
          onReview={handleReview}
          onSelectChoice={sendUserMessage}
          onSend={sendUserMessage}
          status={status}
          upload={{
            memory: briefMemory.memory,
            onFilesChanged: () => void briefMemory.recheckFiles(),
            onUploaded: () => void briefMemory.refetch(),
            onUploadingChange: trackUpload,
            resourceId,
            threadId,
          }}
        />

        <BriefSidebar
          canAddFiles={Boolean(threadId)}
          isCalculating={isScoreCalculating}
          memory={briefMemory.memory}
          onAddAssets={() => setIsUploadOpen(true)}
          onFilesChanged={() => void briefMemory.recheckFiles()}
          quality={quality}
        />
      </div>

      {error ? (
        <p className="px-md pb-lg text-sm lg:px-3xl text-text-error-primary">
          Something went wrong. Please refresh and try again.
        </p>
      ) : null}

      <UploadAssetsModal
        isOpen={isUploadOpen}
        memory={briefMemory.memory}
        onBriefChanged={() => void briefMemory.refetch()}
        onFilesChanged={() => void briefMemory.recheckFiles()}
        onOpenChange={setIsUploadOpen}
        onUploadingChange={trackUpload}
        resourceId={resourceId}
        threadId={threadId}
      />

      <DiscardBriefDialog
        isDiscarding={isDiscarding}
        isOpen={isDiscardOpen}
        onConfirm={() => void confirmSwitchToClassic()}
        onOpenChange={setIsDiscardOpen}
      />
    </>
  );
}
